import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { VendorTotpRow } from "./types";

export function VendorTotpResetPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const listQuery = useQuery<VendorTotpRow[]>({
    queryKey: ["/api/admin/vendor-team-members-with-totp"],
  });

  const resetMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => apiRequest("POST", `/api/admin/vendor-team-members/${id}/reset-totp`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/vendor-team-members-with-totp"] });
      toast({ title: "Two-factor reset", description: "Team member can re-enrol on next login." });
    },
    onError: (err: any) => toast({ title: "Failed to reset 2FA", description: parseErrorMessage(err, "Couldn't reset 2FA. Please try again."), variant: "destructive" }),
  });

  const rows = listQuery.data ?? [];

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-semibold text-base">Vendor Team Member 2FA Resets</h2>
          <p className="text-xs text-muted-foreground">
            Clears the authenticator-app enrolment for vendor team members who lost their device and backup codes.
            They will be required to enrol again on their next login. Every reset is recorded in the admin audit log.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => listQuery.refetch()} disabled={listQuery.isFetching} data-testid="button-refresh-vendor-totp">
          {listQuery.isFetching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Refresh
        </Button>
      </div>
      {listQuery.isLoading ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center" data-testid="text-no-vendor-totp">
          No vendor team members currently have two-factor authentication enabled.
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Vendor</th>
                <th className="text-left px-3 py-2">Team Member</th>
                <th className="text-left px-3 py-2">Contact</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Enrolled</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`row-vendor-totp-${r.id}`}>
                  <td className="px-3 py-2">{r.vendorName}</td>
                  <td className="px-3 py-2">{r.firstName} {r.lastName}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{r.email || r.phone || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.role}</td>
                  <td className="px-3 py-2 text-xs">{r.totpEnrolledAt ? new Date(r.totpEnrolledAt).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => {
                        if (confirm(`Reset two-factor authentication for ${r.firstName} ${r.lastName} (${r.vendorName})? They will be required to enrol again on next login.`)) {
                          resetMutation.mutate(r.id);
                        }
                      }}
                      disabled={resetMutation.isPending}
                      data-testid={`button-reset-vendor-totp-${r.id}`}>
                      {resetMutation.isPending && resetMutation.variables === r.id
                        ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        : <ShieldAlert className="h-3.5 w-3.5 mr-1" />}
                      Reset 2FA
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
